import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  isSameDay,
} from 'date-fns';
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { Header } from '../nav/Header';
import { useProfileSelection } from '../../context/ProfileSelectionContext';
import { PlatformIcon } from '../common/PlatformIcon';
import { GET_POSTS, GET_SOCIAL_PROFILES } from '../../graphql';
import { SocialPost, Platform, TimeSlot, Topic, MediaType } from '../../types';
import { GetSocialPostsResponse, GetSocialProfilesResponse } from '../../types/graphql';
import { cn } from '../ui/utils';
import {
  SimplifiedPostForm,
  SimplifiedPostFormData,
  convertToSimplifiedFormData,
} from '../posts/SimplifiedPostForm';
import { TopicsSection } from '../topics/TopicsSection';

type ViewType = 'week' | 'month';

interface PostStatus {
  status: 'published' | 'uploaded' | 'scheduled' | 'draft';
  label: string;
  color: string;
}

interface PostWithLocalDate extends Omit<SocialPost, 'platform'> {
  localDate: Date;
  platform: string;
}

const truncateText = (text: string | undefined, limit: number = 30): string => {
  if (!text) return '';
  return text.length > limit ? text.substring(0, limit) + '...' : text;
};

const roundToNearestSlot = (date: Date): string => {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const totalMinutes = hours * 60 + minutes;

  const roundedMinutes = Math.floor(totalMinutes / 30) * 30;
  const roundedHours = Math.floor(roundedMinutes / 60);
  const remainingMinutes = roundedMinutes % 60;

  return `${String(roundedHours).padStart(2, '0')}:${String(remainingMinutes).padStart(2, '0')}`;
};

// Helper function for converting timestamps
const toLocalTime = (unixTimestamp: string | number): Date => {
  const timestamp = typeof unixTimestamp === 'string' ? parseInt(unixTimestamp) : unixTimestamp;
  return new Date(timestamp.toString().length > 10 ? timestamp : timestamp * 1000);
};

const calculateRelativePosition = (postTime: Date): string => {
  const minutes = postTime.getMinutes();
  const slotStart = Math.floor(minutes / 30) * 30;
  const relativeMinutes = minutes - slotStart;
  const percentage = (relativeMinutes / 30) * 100;
  return `${percentage}%`;
};

const getPostStatus = (post: PostWithLocalDate | SocialPost): PostStatus => {
  if (post.platformPostId && post.socialPublishDate) {
    return { status: 'published', label: 'Published', color: '#4caf50' };
  }

  if (post.platformPostId && !post.socialPublishDate) {
    return { status: 'uploaded', label: 'Uploaded', color: '#2196f3' };
  }

  if (post.scheduleTime && !post.platformPostId && !post.socialPublishDate) {
    return { status: 'scheduled', label: 'Scheduled', color: '#ff9800' };
  }

  if (
    post.isDraft === true ||
    (!post.scheduleTime && !post.platformPostId && !post.socialPublishDate)
  ) {
    return { status: 'draft', label: 'Draft', color: '#9e9e9e' };
  }

  return { status: 'draft', label: 'Draft', color: '#9e9e9e' };
};

const getPlatformColor = (platform: string): string => {
  const colors: Record<string, string> = {
    facebook: '#1877F2',
    instagram: '#E4405F',
    linkedin: '#0A66C2',
    twitter: '#1DA1F2',
    x: '#000000',
    reddit: '#FF4500',
    threads: '#000000',
    whatsapp: '#25D366',
    bluesky: '#0560FF',
    youtube: '#FF0000',
  };
  return colors[platform.toLowerCase()] || '#666666';
};

interface WeekViewProps {
  days: Date[];
  timeSlots: TimeSlot[];
  posts: Record<string, Record<string, PostWithLocalDate[]>>;
  onTimeSlotDrop?: (topic: Topic, scheduledDate: Date) => void;
  onPostClick?: (post: SocialPost) => void;
  onCellClick?: (scheduledDate: Date) => void;
  selectedScheduleTime?: Date | string | number;
}

function WeekView({
  days,
  timeSlots,
  posts,
  onTimeSlotDrop,
  onPostClick,
  onCellClick,
  selectedScheduleTime,
}: WeekViewProps) {
  const currentTimeRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [draggedOverSlot, setDraggedOverSlot] = useState<string | null>(null);

  useEffect(() => {
    if (currentTimeRef.current && containerRef.current) {
      const containerHeight = containerRef.current.clientHeight;
      const offset = containerHeight / 2;
      const elementTop = currentTimeRef.current.offsetTop;
      containerRef.current.scrollTop = elementTop - offset;
    }
  }, []);

  const currentTime = format(new Date(), 'HH:mm');
  const currentSlotIndex = timeSlots.findIndex((slot) => {
    const slotTime = format(slot.date, 'HH:mm');
    return slotTime >= currentTime;
  });

  const handleDragOver = (e: React.DragEvent, dateKey: string, slotKey: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDraggedOverSlot(`${dateKey}-${slotKey}`);
  };

  const handleDragLeave = () => {
    setDraggedOverSlot(null);
  };

  const handleDrop = (e: React.DragEvent, day: Date, slot: TimeSlot) => {
    e.preventDefault();
    setDraggedOverSlot(null);

    const topicData = e.dataTransfer.getData('application/json');
    if (topicData && onTimeSlotDrop) {
      try {
        const topic: Topic = JSON.parse(topicData);
        const slotTime = format(slot.date, 'HH:mm');
        const [hours, minutes] = slotTime.split(':').map(Number);

        const scheduledDate = new Date(day);
        scheduledDate.setHours(hours, minutes, 0, 0);

        onTimeSlotDrop(topic, scheduledDate);
      } catch (error) {
        console.error('Error parsing dropped topic:', error);
      }
    }
  };

  const handleCellClick = (e: React.MouseEvent, day: Date, slot: TimeSlot) => {
    if ((e.target as HTMLElement).closest('[data-post-item]')) {
      return;
    }

    if (onCellClick) {
      const slotTime = format(slot.date, 'HH:mm');
      const [hours, minutes] = slotTime.split(':').map(Number);

      const scheduledDate = new Date(day);
      scheduledDate.setHours(hours, minutes, 0, 0);

      onCellClick(scheduledDate);
    }
  };

  const isCellSelected = (day: Date, slot: TimeSlot): boolean => {
    if (!selectedScheduleTime) return false;

    const selectedDate =
      selectedScheduleTime instanceof Date ? selectedScheduleTime : new Date(selectedScheduleTime);

    const cellDate = new Date(day);
    const slotTime = format(slot.date, 'HH:mm');
    const [hours, minutes] = slotTime.split(':').map(Number);
    cellDate.setHours(hours, minutes, 0, 0);

    const slotEndTime = new Date(cellDate);
    slotEndTime.setMinutes(slotEndTime.getMinutes() + 30);

    const isSameDay =
      selectedDate.getFullYear() === cellDate.getFullYear() &&
      selectedDate.getMonth() === cellDate.getMonth() &&
      selectedDate.getDate() === cellDate.getDate();

    if (!isSameDay) return false;

    return selectedDate >= cellDate && selectedDate < slotEndTime;
  };

  return (
    <div
      ref={containerRef}
      className="w-full grid border-t-[0.5px] border-gray-300/30 box-border"
      style={{
        gridTemplateColumns: 'minmax(80px, auto) repeat(7, 1fr)',
        gridTemplateRows: 'auto repeat(48, minmax(40px, auto))',
      }}
    >
      <div className="border-r-[0.5px] border-b-[0.5px] border-gray-300/30 p-1 h-12 bg-cmp-surface sticky top-0 z-[100] box-border" />

      {days.map((day, dayIndex) => (
        <div
          key={format(day, 'yyyy-MM-dd')}
          className={cn(
            'border-r-[0.5px] border-b-[0.5px] border-t-[0.5px] border-gray-300/30 p-1 h-12 bg-cmp-surface sticky top-0 z-[100] flex items-center justify-center box-border',
            dayIndex === 0 && 'border-l-[0.5px] border-gray-300/30'
          )}
        >
          <p className="text-sm font-medium text-black">{format(day, 'EEE MMM d')}</p>
        </div>
      ))}

      {timeSlots.map((slot, index) => (
        <React.Fragment key={slot.key}>
          <div
            ref={index === currentSlotIndex ? currentTimeRef : null}
            className={cn(
              'border-r-[0.5px] border-t-[0.5px] border-gray-300/30 px-2 flex items-start justify-end sticky left-0 bg-cmp-surface z-[9] box-border -pt-1',
              index === timeSlots.length - 1 && 'border-b-[0.5px] border-gray-300/30'
            )}
            style={{
              borderTopStyle: index % 4 === 0 ? 'solid' : 'dashed',
              borderTopWidth: '0.5px',
            }}
          >
            <p className="relative -top-2.5 bg-cmp-surface pl-1 text-xs text-black">{slot.label}</p>
          </div>

          {days.map((day, dayIndex) => {
            const dateKey = format(day, 'yyyy-MM-dd');
            const slotPosts = (posts[dateKey] && posts[dateKey][slot.key]) || [];
            const hasMultiplePosts = slotPosts.length > 1;
            const slotKey = `${dateKey}-${slot.key}`;
            const isDraggedOver = draggedOverSlot === slotKey;
            const isSelected = isCellSelected(day, slot);

            return (
              <div
                key={slotKey}
                onClick={(e) => handleCellClick(e, day, slot)}
                onDragOver={(e) => handleDragOver(e, dateKey, slot.key)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, day, slot)}
                className={cn(
                  'border-r-[0.5px] border-t-[0.5px] border-gray-300/30 p-1 hover:bg-cmp-bronze-soft/20 box-border relative cursor-pointer transition-colors duration-200 min-w-0 overflow-visible',
                  dayIndex === 0 && 'border-l-[0.5px] border-gray-300/30',
                  index === timeSlots.length - 1 && 'border-b-[0.5px] border-gray-300/30'
                )}
                style={{
                  borderTopStyle: index % 4 === 0 ? 'solid' : 'dashed',
                  borderTopWidth: '0.5px',
                  backgroundColor: isDraggedOver
                    ? 'rgba(0, 97, 254, 0.45)'
                    : isSelected
                      ? 'rgba(43, 171, 175, 0.3)'
                      : 'transparent',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isDraggedOver
                    ? 'rgba(0, 97, 254, 0.85)'
                    : isSelected
                      ? 'rgba(43, 171, 175, 0.5)'
                      : 'rgba(0, 97, 254, 0.45)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isDraggedOver
                    ? 'rgba(0, 97, 254, 0.45)'
                    : isSelected
                      ? 'rgba(43, 171, 175, 0.3)'
                      : 'transparent';
                }}
              >
                {slotPosts.length > 0 && (
                  <div
                    className="flex flex-row gap-1 items-stretch z-10"
                    style={{
                      position: 'absolute',
                      top: calculateRelativePosition(slotPosts[0].localDate),
                      left: 0,
                      right: 0,
                      width: '100%',
                    }}
                  >
                    {slotPosts.map((post) => {
                      const postWithLocalDate = post as PostWithLocalDate;
                      const postStatus = getPostStatus(post);

                      return (
                        <div
                          key={`post-${post.id}`}
                          data-post-item
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onPostClick) {
                              onPostClick({
                                ...post,
                                platform: post.platform as Platform,
                              } as SocialPost);
                            }
                          }}
                          className={`relative px-1 py-2 text-white text-sm overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer flex items-center box-border hover:opacity-90 hover:scale-[1.02] transition-all duration-200 rounded ${
                            hasMultiplePosts ? 'flex-1 min-w-0' : 'w-full'
                          }`}
                          style={{
                            backgroundColor: getPlatformColor(postWithLocalDate.platform),
                          }}
                        >
                          <div className="flex items-center gap-1 min-w-0 w-full">
                            <div className="flex items-center flex-shrink-0">
                              <PlatformIcon
                                platform={postWithLocalDate.platform as Platform}
                                size={18}
                              />
                            </div>
                            <p className="flex-1 overflow-hidden text-ellipsis text-sm whitespace-nowrap leading-snug font-medium">
                              {truncateText(
                                post.title || post.summary || '',
                                hasMultiplePosts ? 15 : 30
                              )}
                            </p>
                          </div>
                          {/* Colored corner indicator */}
                          <div
                            className="absolute top-0 right-0 z-[11]"
                            style={{
                              width: '10px',
                              height: '10px',
                              backgroundColor: postStatus.color,
                              borderTopRightRadius: '2px',
                              clipPath: 'polygon(100% 0, 100% 100%, 0 0)',
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
}

interface MonthViewProps {
  days: Date[];
  posts: Record<string, Record<string, PostWithLocalDate[]>>;
  onWeekSelect: (weekStart: Date) => void;
  currentDate: Date;
}

function MonthView({ days, posts, onWeekSelect, currentDate }: MonthViewProps) {
  console.log('MonthView received posts:', posts);

  const tooltipRef = useRef<HTMLDivElement>(null);
  const [hoveredPost, setHoveredPost] = useState<PostWithLocalDate | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const getPostsForDate = (date: Date) => {
    const dateKey = format(date, 'yyyy-MM-dd');
    if (!posts[dateKey]) return [];
    return Object.values(posts[dateKey]).flat() as PostWithLocalDate[];
  };

  const calculateTooltipPosition = (clientX: number, clientY: number) => {
    if (!tooltipRef.current) return;

    const tooltip = tooltipRef.current;
    const tooltipWidth = tooltip.offsetWidth;
    const tooltipHeight = tooltip.offsetHeight;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const offset = 10;

    let left = clientX + offset;
    let top = clientY + offset;

    // Adjust if tooltip would go off screen
    if (left + tooltipWidth > windowWidth) {
      left = clientX - tooltipWidth - offset;
    }
    if (top + tooltipHeight > windowHeight) {
      top = clientY - tooltipHeight - offset;
    }

    setTooltipPosition({ x: left, y: top });
  };

  const handlePostMouseEnter = (e: React.MouseEvent, post: PostWithLocalDate) => {
    setHoveredPost(post);
    setTimeout(() => {
      calculateTooltipPosition(e.clientX, e.clientY);
    }, 0);
  };

  const handlePostMouseLeave = (e: React.MouseEvent) => {
    // Dismiss tooltip when leaving the dot
    e.stopPropagation();
    setHoveredPost(null);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (hoveredPost) {
      calculateTooltipPosition(e.clientX, e.clientY);
    }
  };

  const handleClick = () => {
    setHoveredPost(null);
  };

  // Custom Day component to render posts with platform info
  const CustomDay = (props: any) => {
    // react-day-picker v9 passes 'day' as a CalendarDay object with a 'date' property
    const dayObj = props.day;
    const date = dayObj?.date;
    const outside = dayObj?.outside || props.modifiers?.outside || false;
    const className = props.className || '';

    // If we don't have a valid date, render empty cell
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return (
        <div
          className={cn(
            'relative min-h-28 p-1 border-r-[0.5px] border-b-[0.5px] border-gray-300/30 opacity-50',
            className
          )}
        />
      );
    }

    const isCurrentMonth = !outside;
    const postsOnDay = isCurrentMonth ? getPostsForDate(date) : [];
    const dayNumber = format(date, 'd');
    const isToday = isSameDay(date, new Date());
    const isFirstColumn = format(date, 'EEE') === 'Sun';

    // Check if this is in the last row of the calendar (6th row)
    const monthStart = startOfMonth(currentDate);
    const firstWeekStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const dateWeekStart = startOfWeek(date, { weekStartsOn: 0 });

    // Calculate the week number (0-indexed) - first week is 0, last week (6th row) is 5
    const weeksDiff = Math.floor(
      (dateWeekStart.getTime() - firstWeekStart.getTime()) / (7 * 24 * 60 * 60 * 1000)
    );
    const isLastRow = weeksDiff === 5; // 6th row (0-indexed = 5)

    // Simple border rules: all cells have top and right, first column has left, last row has bottom
    const borderLeft = isFirstColumn ? 'border-l-[0.5px] border-gray-300/30' : 'border-l-0';
    const borderRight = 'border-r-[0.5px] border-gray-300/30';
    const borderTop = 'border-t-[0.5px] border-gray-300/30';
    const borderBottom = isLastRow ? 'border-b-[0.5px] border-gray-300/30' : '';

    return (
      <button
        type="button"
        className={cn(
          'relative min-h-28 p-1 text-left w-full h-full flex flex-col',
          borderLeft,
          borderRight,
          borderTop,
          borderBottom,
          'hover:bg-cmp-bronze-soft/20 transition-colors',
          !isCurrentMonth && 'opacity-50',
          className
        )}
        style={{
          backgroundColor: outside ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
          ...(isLastRow ? { borderBottom: '0.5px solid #e5e7eb' } : {}),
        }}
        aria-label={format(date, 'PPP')}
        onClick={(e) => {
          e.stopPropagation();
          // Find the week start for this day
          const weekStart = startOfWeek(date);
          onWeekSelect(weekStart);
        }}
      >
        <div
          className="absolute inline-flex items-center justify-center"
          style={{
            color: isToday ? '#ffffff' : '#e5e5e5',
            fontWeight: 'bold',
            fontSize: '12px',
            display: 'inline-flex',
            visibility: 'visible',
            width: isToday ? '24px' : 'auto',
            height: isToday ? '24px' : 'auto',
            minWidth: isToday ? '24px' : 'auto',
            minHeight: isToday ? '24px' : 'auto',
            borderRadius: isToday ? '50%' : '0',
            backgroundColor: isToday ? '#2babaf' : 'transparent',
            top: '2px',
            right: '4px',
            zIndex: 10,
          }}
        >
          {dayNumber}
        </div>
        {isCurrentMonth && postsOnDay.length > 0 && (
          <div
            className="mt-1 flex-1 overflow-y-auto pt-6 w-full flex flex-wrap gap-1.5 items-start z-10 relative"
            style={{ paddingLeft: '6px', paddingRight: '6px' }}
            onMouseMove={handleMouseMove}
            onMouseLeave={(e) => {
              // Dismiss tooltip when leaving the posts container area
              if (
                !e.relatedTarget ||
                !(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)
              ) {
                setHoveredPost(null);
              }
            }}
          >
            {postsOnDay.map((post) => {
              const postWithLocalDate = post as PostWithLocalDate;
              return (
                <div
                  key={post.id}
                  className="rounded-full cursor-pointer hover:scale-125 transition-transform relative z-10"
                  style={{
                    backgroundColor: getPlatformColor(postWithLocalDate.platform),
                    width: '10px',
                    height: '10px',
                  }}
                  onMouseEnter={(e) => handlePostMouseEnter(e, postWithLocalDate)}
                  onMouseLeave={handlePostMouseLeave}
                  onClick={(e) => e.stopPropagation()}
                />
              );
            })}
          </div>
        )}
      </button>
    );
  };

  return (
    <>
      <div className="flex flex-col h-full" onClick={handleClick}>
        <style>{`
        [class*="rdp"] table,
        [class*="rdp"] table thead,
        [class*="rdp"] table tbody,
        [class*="rdp"] table tr {
          display: block !important;
        }
        [class*="rdp"] table thead tr,
        [class*="rdp"] table tbody tr {
          display: flex !important;
          width: 100% !important;
        }
        [class*="rdp"] table thead th,
        [class*="rdp"] table tbody td {
          display: block !important;
          flex: 1 1 0% !important;
          min-width: 0 !important;
          width: calc(100% / 7) !important;
          max-width: calc(100% / 7) !important;
        }
        [class*="rdp"] table tbody td {
          border-top: 0.5px dashed rgba(209, 213, 219, 0.3) !important;
          border-right: 0.5px dashed rgba(209, 213, 219, 0.3) !important;
          border-bottom: none !important;
          border-left: none !important;
        }
        [class*="rdp"] table tbody tr td:first-child {
          border-left: 0.5px dashed rgba(209, 213, 219, 0.3) !important;
        }
        [class*="rdp"] table tbody tr:last-child td {
          border-bottom: 0.5px dashed rgba(209, 213, 219, 0.3) !important;
          border-bottom-width: 0.5px !important;
          border-bottom-style: dashed !important;
          border-bottom-color: rgba(209, 213, 219, 0.3) !important;
        }
        [class*="rdp"] table tbody tr:last-child td button,
        [class*="rdp"] table tbody tr:last-child td > *,
        [class*="rdp"] table tbody tr:last-child td div {
          border-bottom: 0.5px dashed rgba(209, 213, 219, 0.3) !important;
          border-bottom-width: 0.5px !important;
          border-bottom-style: dashed !important;
          border-bottom-color: rgba(209, 213, 219, 0.3) !important;
        }
        .rdp-month_caption {
          display: none !important;
        }
        [class*="rdp"] table thead th {
          background-color: var(--ui-n-200) !important; /* calendar chrome band */
          color: var(--ui-n-950) !important;
          border-right: 0.5px dashed rgba(209, 213, 219, 0.3) !important;
          border-bottom: 0.5px dashed rgba(209, 213, 219, 0.3) !important;
          padding: 0.25rem !important; /* p-1 */
          height: 3rem !important; /* h-12 */
          font-size: 0.875rem !important; /* text-sm */
          font-weight: 500 !important; /* font-medium */
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          position: relative !important;
          z-index: 999 !important;
        }
        [class*="rdp"] table thead th:last-child {
          border-right: none !important;
        }
        [class*="rdp"] table thead th:first-child {
          border-left: 0.5px dashed rgba(209, 213, 219, 0.3) !important;
        }
        [class*="rdp"] table tbody tr td:first-child button {
          border-left: 0.5px dashed rgba(209, 213, 219, 0.3) !important;
        }
        [class*="rdp"] table tbody tr:last-child td button {
          border-bottom: 0.5px dashed rgba(209, 213, 219, 0.3) !important;
        }
      `}</style>
        <DayPicker
          mode="single"
          month={currentDate}
          className="p-0 flex-1"
          classNames={{
            months: 'flex flex-col h-full',
            month: 'flex flex-col gap-0 w-full h-full',
            caption: 'hidden',
            caption_label: '',
            nav: 'hidden',
            nav_button: '',
            nav_button_previous: '',
            nav_button_next: '',
            table: 'w-full border-separate border-spacing-0 h-full',
            head_row: 'flex border-b border-gray-300/30',
            head_cell:
              'text-sm font-medium flex-1 p-1 h-12 text-center bg-cmp-surface text-black border-r border-b border-gray-300/30 last:border-r-0 flex items-center justify-center box-border',
            row: 'flex w-full flex-1',
            cell: 'flex-1 relative p-0 text-center text-sm focus-within:relative border-r-[0.5px] border-b-[0.5px] border-gray-300/30 border-t-0 min-h-28 h-full',
            day: 'h-full p-0 w-full',
            day_selected: '',
            day_today: '',
            day_outside: '',
            day_disabled: '',
            day_range_middle: '',
            day_hidden: 'invisible',
          }}
          formatters={{
            formatWeekdayName: (date) => format(date, 'EEE'),
          }}
          components={{
            Day: CustomDay,
          }}
          showOutsideDays={true}
          fixedWeeks={true}
        />
      </div>

      {hoveredPost && (
        <div
          ref={tooltipRef}
          className="fixed w-80 max-w-[90vw] max-h-[calc(100vh-20px)] overflow-y-auto z-[5000] shadow-2xl pointer-events-none bg-core-dark rounded border border-gray-600"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
          }}
        >
          <div className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <PlatformIcon platform={hoveredPost.platform as Platform} size={20} />
              <h6 className="text-base font-bold text-white">
                {hoveredPost.title || 'Untitled Post'}
              </h6>
            </div>

            {hoveredPost.summary && (
              <>
                <hr className="my-2 border-gray-600" />
                <p className="mb-2 text-sm text-gray-400">{hoveredPost.summary}</p>
              </>
            )}

            {hoveredPost.postUrl && (
              <>
                <hr className="my-2 border-gray-600" />
                <p className="mb-2 text-sm text-white">
                  <strong>URL:</strong>{' '}
                  <a
                    href={hoveredPost.postUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary no-underline break-all hover:underline"
                  >
                    {hoveredPost.postUrl}
                  </a>
                </p>
              </>
            )}

            {hoveredPost.thoughts && (
              <>
                <hr className="my-2 border-gray-600" />
                <p className="mb-2 text-sm text-white">
                  <strong>Thoughts:</strong> {hoveredPost.thoughts}
                </p>
              </>
            )}

            <hr className="my-2 border-gray-600" />
            <div className="flex justify-between items-center">
              <p className="text-xs text-gray-400">
                <strong>Platform:</strong> {hoveredPost.platform?.toUpperCase() || 'Unknown'}
              </p>
              <p className="text-xs text-gray-400">
                {format(hoveredPost.localDate, 'MMM d, yyyy h:mm a')}
              </p>
            </div>

            {hoveredPost.socialProfile?.profileName && (
              <p className="text-xs text-gray-400 block mt-1">
                <strong>Profile:</strong> {hoveredPost.socialProfile.profileName}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export function Calendar() {
  const [view, setView] = useState<ViewType>('week');
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [selectedWeek, setSelectedWeek] = useState<Date | null>(null);
  const [selectedPlatform, setSelectedPlatform] = useState<string>('');
  const { selectedProfiles, setSelectedProfiles, selectedAccount, setSelectedAccount } =
    useProfileSelection();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [selectedPost, setSelectedPost] = useState<SocialPost | null>(null);
  const [mediaFilters, setMediaFilters] = useState<MediaType[]>([
    'IMAGE',
    'VIDEO',
    'GALLERY',
    'PHOTO',
    'BOOKMARK',
  ]);
  const [formData, setFormData] = useState<SimplifiedPostFormData>({
    message: '',
    postType: '',
    mediaUrl: '',
    linkUrl: '',
    scheduleTime: new Date(),
    isStory: false,
    notes: '',
    isDraft: false,
  });
  const [selectedFormProfiles, setSelectedFormProfiles] = useState<Set<string>>(new Set());
  const [showPostFormModal, setShowPostFormModal] = useState<boolean>(false);
  const [topicsRefetch, setTopicsRefetch] = useState<(() => Promise<any>) | null>(null);

  const dateRange = useMemo(() => {
    if (view === 'week' || selectedWeek) {
      const weekStart = selectedWeek ? selectedWeek : startOfWeek(currentDate);
      const weekEnd = endOfWeek(weekStart);
      return { start: weekStart, end: weekEnd };
    } else {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      return { start: monthStart, end: monthEnd };
    }
  }, [currentDate, view, selectedWeek]);

  const { data: socialProfilesData } = useQuery<GetSocialProfilesResponse>(GET_SOCIAL_PROFILES, {
    variables: { accountId: selectedAccount?.id },
    skip: !selectedAccount?.id,
  });

  // Filter selected profiles by platform if a platform is selected
  useEffect(() => {
    if (socialProfilesData?.socialProfiles && selectedPlatform) {
      const activeProfilesForPlatform = socialProfilesData.socialProfiles
        .filter((profile) => profile.platform === selectedPlatform && profile.isActive)
        .map((profile) => profile.id);

      // Only update if we have profiles for this platform and current selection doesn't match
      const currentPlatformProfiles = Array.from(selectedProfiles).filter((id) => {
        const profile = socialProfilesData.socialProfiles.find((p) => p.id === id);
        return profile?.platform === selectedPlatform;
      });

      if (activeProfilesForPlatform.length > 0 && currentPlatformProfiles.length === 0) {
        setSelectedProfiles(new Set(activeProfilesForPlatform));
      }
    }
  }, [socialProfilesData, selectedPlatform, selectedProfiles, setSelectedProfiles]);

  const { data: postsData } = useQuery<GetSocialPostsResponse>(GET_POSTS, {
    variables: {
      socialProfileIds: Array.from(selectedProfiles),
    },
    skip: selectedProfiles.size === 0,
  });

  const handlePrevious = () => {
    if (view === 'week') {
      setCurrentDate(subWeeks(currentDate, 1));
    } else {
      setCurrentDate(subMonths(currentDate, 1));
    }
  };

  const handleNext = () => {
    if (view === 'week') {
      setCurrentDate(addWeeks(currentDate, 1));
    } else {
      setCurrentDate(addMonths(currentDate, 1));
    }
  };

  const handleViewChange = (event: any, newView: ViewType | null) => {
    if (newView !== null) {
      setView(newView);
      setSelectedWeek(null);
      console.log('hello view Changed');
    }
  };

  const handleWeekSelect = (weekStart: Date) => {
    setSelectedWeek(weekStart);
    setView('week');
  };

  const timeSlots = useMemo<TimeSlot[]>(() => {
    const slots: TimeSlot[] = [];
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);

    for (let i = 0; i < 48; i++) {
      const hour = Math.floor(i / 2);
      const minutes = (i % 2) * 30;
      const slot = new Date(startOfDay);
      slot.setHours(hour, minutes, 0, 0);
      slots.push({
        date: slot,
        key: format(slot, 'HH:mm'),
        label: format(slot, 'h:mm a'),
      });
    }
    return slots;
  }, []);

  const days = useMemo(() => {
    return eachDayOfInterval({
      start: dateRange.start,
      end: dateRange.end,
    });
  }, [dateRange]);

  const organizedPosts = useMemo<Record<string, Record<string, PostWithLocalDate[]>>>(() => {
    if (!postsData?.socialPosts) return {};

    const organized: Record<
      string,
      Record<string, PostWithLocalDate[]>
    > = postsData.socialPosts.reduce(
      (acc, post) => {
        const localDate = toLocalTime(post.scheduleTime);
        const date = format(localDate, 'yyyy-MM-dd');
        const roundedTime = roundToNearestSlot(localDate);

        if (!acc[date]) acc[date] = {};
        if (!acc[date][roundedTime]) acc[date][roundedTime] = [];

        acc[date][roundedTime].push({
          ...post,
          platform: post.platform.toLowerCase(),
          localDate,
        } as PostWithLocalDate);

        return acc;
      },
      {} as Record<string, Record<string, PostWithLocalDate[]>>
    );

    return organized;
  }, [postsData]);

  const handlePlatformChange = (event: any, newPlatform: string | string[] | null) => {
    setSelectedPlatform((newPlatform as string) || '');
  };

  // Update form profiles when selected profiles or platform changes
  useEffect(() => {
    if (socialProfilesData?.socialProfiles) {
      const activeProfilesForPlatform = socialProfilesData.socialProfiles
        .filter((profile) => profile.platform === selectedPlatform && profile.isActive)
        .map((profile) => profile.id);

      // Only auto-select platform profiles if no profiles are selected at all
      if (selectedProfiles.size === 0 && activeProfilesForPlatform.length > 0) {
        setSelectedProfiles(new Set(activeProfilesForPlatform));
      }

      if (activeProfilesForPlatform.length === 1) {
        setSelectedFormProfiles(new Set(activeProfilesForPlatform));
      } else {
        setSelectedFormProfiles(new Set());
      }
    }
  }, [socialProfilesData, selectedPlatform, selectedProfiles, setSelectedProfiles]);

  const handleTopicSelect = (topic: Topic) => {
    setEditMode(false);
    setSelectedPost(null);

    // Initialize with profiles from selectedProfiles
    setSelectedFormProfiles(new Set(Array.from(selectedProfiles)));

    const postType =
      topic.mediaType === 'VIDEO'
        ? 'video'
        : topic.mediaType === 'BOOKMARK'
          ? 'link'
          : topic.mediaType === 'IMAGE' || topic.mediaType === 'PHOTO'
            ? 'photo'
            : 'link';

    setFormData({
      message: topic.summary || '',
      postType: postType as any,
      mediaUrl: topic.mediaUrl || '',
      linkUrl: topic.destinationUrl || '',
      scheduleTime: new Date(),
      isStory: false,
      notes: '',
      isDraft: false,
    });
    setShowPostFormModal(true);
  };

  const handleTopicDrop = (topic: Topic, scheduledDate: Date) => {
    setEditMode(false);
    setSelectedPost(null);

    // Initialize with profiles from selectedProfiles
    setSelectedFormProfiles(new Set(Array.from(selectedProfiles)));

    const postType =
      topic.mediaType === 'VIDEO'
        ? 'video'
        : topic.mediaType === 'BOOKMARK'
          ? 'link'
          : topic.mediaType === 'IMAGE' || topic.mediaType === 'PHOTO'
            ? 'photo'
            : 'link';

    setFormData({
      message: topic.summary || '',
      postType: postType as any,
      mediaUrl: topic.mediaUrl || '',
      linkUrl: topic.destinationUrl || '',
      scheduleTime: scheduledDate,
      isStory: false,
      notes: '',
      isDraft: false,
    });
    setShowPostFormModal(true);
  };

  const handleEditPost = (post: SocialPost) => {
    setSelectedPost(post);
    setEditMode(true);
    setFormData(
      convertToSimplifiedFormData({
        title: post.title || '',
        summary: post.summary || '',
        postUrl: post.postUrl || '',
        imageUrl: post.imageUrl || '',
        thoughts: post.thoughts || '',
        postType: post.postType || '',
        platform: post.platform || '',
        scheduleTime: toLocalTime(post.scheduleTime),
        notes: post.notes,
        isDraft: post.isDraft,
      })
    );
    // Initialize selectedFormProfiles with the post's profile
    if (post.socialProfileId) {
      setSelectedFormProfiles(new Set([post.socialProfileId]));
    } else {
      setSelectedFormProfiles(new Set());
    }
    setShowPostFormModal(true);
  };

  const handleMediaFilterChange = (event: any, newFilters: MediaType[]) => {
    if (newFilters.length > 0) {
      setMediaFilters(newFilters);
    }
  };

  const handleRefresh = async () => {
    try {
      if (topicsRefetch && typeof topicsRefetch === 'function') {
        await topicsRefetch();
      }
    } catch (error) {
      console.error('Error refreshing topics:', error);
    }
  };

  const handleCellClick = (scheduledDate: Date) => {
    setEditMode(false);
    setSelectedPost(null);

    // Initialize with profiles from selectedProfiles
    setSelectedFormProfiles(new Set(Array.from(selectedProfiles)));

    setFormData({
      message: '',
      postType: '',
      mediaUrl: '',
      linkUrl: '',
      scheduleTime: scheduledDate,
      isStory: false,
      notes: '',
      isDraft: false,
    });
    setShowPostFormModal(true);
  };

  const handleClosePostFormModal = () => {
    setShowPostFormModal(false);
    setEditMode(false);
    setSelectedPost(null);
    setSelectedFormProfiles(new Set());
    setFormData({
      message: '',
      postType: '',
      mediaUrl: '',
      linkUrl: '',
      scheduleTime: new Date(),
      isStory: false,
      notes: '',
      isDraft: false,
    });
  };

  return (
    <div className="ink-page flex w-full flex-col h-full min-h-0 overflow-hidden">
      <div className="flex flex-col flex-1 overflow-y-auto md:overflow-hidden">
        <Header
          showPlatformToggle={true}
          onPlatformChange={handlePlatformChange}
          selectedProfiles={selectedProfiles}
          onProfileToggle={setSelectedProfiles}
          selectedAccount={selectedAccount}
          onAccountChange={setSelectedAccount}
        />
        <div className="w-full max-w-full overflow-y-auto md:overflow-hidden relative flex-1 flex flex-col min-h-0">
          <div className="p-1 m-0 w-full flex-1 relative overflow-y-auto md:overflow-hidden box-border bg-primary-background flex flex-col min-h-0">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-1 transition-all duration-200 box-border w-full flex-1 min-h-0">
              <div className="col-span-12 md:col-span-8 flex flex-col min-h-0">
                <div className="bg-core-dark p-6 h-full flex flex-col min-h-0">
                  <div className="flex items-center justify-center mb-6 flex-shrink-0 relative">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handlePrevious}
                        className="p-2 rounded hover:bg-form-dark transition-colors"
                      >
                        <ChevronLeft className="w-9 h-9 text-white" />
                      </button>

                      <h5 className="text-xl font-semibold text-white">
                        {view === 'week'
                          ? `Week of ${format(dateRange.start, 'MMMM d, yyyy')}`
                          : format(currentDate, 'MMMM yyyy')}
                      </h5>

                      <button
                        onClick={handleNext}
                        className="p-2 rounded hover:bg-form-dark transition-colors"
                      >
                        <ChevronRight className="w-9 h-9 text-white" />
                      </button>
                    </div>

                    <div className="absolute right-0 flex items-center gap-2">
                      <button
                        onClick={handleRefresh}
                        className="p-1 rounded text-primary-light hover:bg-primary-dark transition-colors"
                        title="Refresh topics"
                      >
                        <RefreshCw className="w-5 h-5" />
                      </button>
                      <div className="inline-flex rounded border border-gray-600">
                        <button
                          onClick={() => handleViewChange(null, 'week')}
                          className={`px-4 py-2 text-sm font-medium transition-colors ${
                            view === 'week'
                              ? 'bg-primary text-onfill'
                              : 'bg-core-dark text-white hover:bg-form-dark'
                          } ${view === 'week' ? 'rounded-l' : ''}`}
                        >
                          Week
                        </button>
                        <button
                          onClick={() => handleViewChange(null, 'month')}
                          className={`px-4 py-2 text-sm font-medium transition-colors ${
                            view === 'month'
                              ? 'bg-primary text-onfill'
                              : 'bg-core-dark text-white hover:bg-form-dark'
                          } ${view === 'month' ? 'rounded-r' : ''}`}
                        >
                          Month
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 min-h-0 relative overflow-hidden">
                    <div className="absolute inset-0 overflow-y-auto overflow-x-auto">
                      {view === 'week' ? (
                        <WeekView
                          days={days}
                          timeSlots={timeSlots}
                          posts={organizedPosts}
                          onTimeSlotDrop={handleTopicDrop}
                          onPostClick={handleEditPost}
                          onCellClick={handleCellClick}
                          selectedScheduleTime={formData.scheduleTime}
                        />
                      ) : (
                        <MonthView
                          days={days}
                          posts={organizedPosts}
                          onWeekSelect={handleWeekSelect}
                          currentDate={currentDate}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-span-12 md:col-span-4 flex flex-col min-h-0">
                <TopicsSection
                  accountId={selectedAccount?.id}
                  onTopicSelect={handleTopicSelect}
                  mediaFilters={mediaFilters}
                  onMediaFilterChange={handleMediaFilterChange}
                  selectedAccount={selectedAccount}
                  selectedPost={selectedPost}
                  onRefetchReady={setTopicsRefetch}
                  clickBehavior="details"
                />
              </div>
            </div>
          </div>
        </div>

        {/* PostForm Modal */}
        {showPostFormModal && (
          <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-cmp-raise rounded-cmp3 shadow-cmp-e3 border border-cmp-line max-w-4xl w-full max-h-[90vh] overflow-auto m-4">
              <SimplifiedPostForm
                selectedPlatforms={selectedPlatform as Platform}
                selectedProfiles={selectedProfiles}
                selectedAccount={selectedAccount}
                selectedPost={selectedPost}
                setSelectedPost={setSelectedPost}
                socialProfilesData={socialProfilesData}
                editMode={editMode}
                setEditMode={setEditMode}
                formData={formData}
                setFormData={setFormData}
                selectedFormProfiles={selectedFormProfiles}
                setSelectedFormProfiles={setSelectedFormProfiles}
                onClose={handleClosePostFormModal}
                accountId={selectedAccount?.id}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
